/**
 * ControlPanel Component
 *
 * Provides interactive controls for the 3D model viewer including:
 * - Tool selection (area vs distance measurement)
 * - Camera controls (center/reset view)
 * - Measurement controls (undo, reset points)
 * - Visual customization (color selection)
 * - Depth estimation controls
 */

import { PropsWithChildren, useContext } from "react";

import { Divider, SelectChangeEvent, Stack, useTheme } from "@mui/material";

import StraightenIcon from "@mui/icons-material/Straighten";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";
import TimelineIcon from "@mui/icons-material/Timeline";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CancelIcon from "@mui/icons-material/Cancel";
import CalculateIcon from "@mui/icons-material/Calculate";
import { Vector3 } from "three";

import ColorSelector from "./components/color-selector";
import UploadPointsButton from "./components/upload-points-button";
import DownloadPointsButton from "./components/download-points-button";
import {
  DepthEstimationStatus,
  ModelViewerContext,
} from "../../../context/model-viewer-context";
import ControlPanelButton from "./components/control-panel-button";

/**
 * Props for the ControlPanel component
 */
interface ControlPanelProps {
  // Tool state and controls
  /** Whether the area measurement tool is currently active */
  showAreaTool: boolean;
  /** Callback to toggle area measurement tool */
  onAreaToolToggle: () => void;
  /** Callback to toggle distance measurement tool */
  onMeasureToolToggle: () => void;

  // View controls
  /** Callback to center the model in the view */
  onCenterModel: () => void;

  // Point management
  /** Callback to undo the last measurement point */
  onUndo: () => void;
  /** Whether reset functionality is available (has points to reset) */
  canReset: boolean;
  /** Callback to reset all measurement points */
  onReset: () => void;

  // Visual customization
  /** Current measurement line color */
  color: string;
  /** Callback to change measurement line color */
  setColor: (color: string) => void;

  // Depth estimation
  /** Whether depth calculation can be performed (requires 3+ points) */
  canCalculateDepth: boolean;
  /** Whether to show depth estimation controls */
  showDepthEstimation?: boolean;

  // Data and file handling
  /** Patient identifier for file naming */
  patientId: string;
  /** Is model loaded in viewer */
  modelLoaded: boolean
  /** Array of measurement points placed on the model */
  points: Vector3[];
  /** Calculated area from measurement points */
  area: number;
  /** Callback when points are uploaded from a file */
  onPointsUploaded: (points: Vector3[]) => void;
}

const ControlPanel: React.FC<
  PropsWithChildren<ControlPanelProps>
> = ({
  showAreaTool,
  onAreaToolToggle,
  onMeasureToolToggle,
  onCenterModel,
  onUndo,
  canReset,
  onReset,
  color,
  setColor,
  canCalculateDepth,
  showDepthEstimation = false,
  patientId,
  modelLoaded,
  points,
  area,
  onPointsUploaded,
}) => {
    // Context and theme setup
    const {
      setDepthStatus,
      depthStatus: waitingForDepth,
      isLoading,
    } = useContext(ModelViewerContext);

    // Calculate button states based on current conditions
    const uploadDisabled = points.length > 0 || !modelLoaded || isLoading;
    const downloadDisabled = points.length < 3 || !modelLoaded || isLoading;

    /**
     * Handles color selection change from the color selector
     * @param event - Select change event containing the new color value
     */
    const handleColorChange = (event: SelectChangeEvent<string>) => {
      setColor(event.target.value as string);
    };

    return (
      <Stack
        direction={"column"}
        spacing={1}
        width={"fit-content"}
        alignItems={"center"}
        alignContent={"center"}
      >
        {/* Measurement Tool Selection */}
        <ControlPanelButton
          disabled={!modelLoaded}
          active={showAreaTool}
          icon={<TimelineIcon />}
          onClick={onAreaToolToggle}
          activeTooltip="Turn off Area Tool"
          inactiveTooltip="Turn on Area Tool"
          tooltipPlacement="right"
        />

        <ControlPanelButton
          disabled={!modelLoaded}
          active={!showAreaTool}
          icon={<StraightenIcon />}
          onClick={onMeasureToolToggle}
          activeTooltip="Turn off Measure Tool"
          inactiveTooltip="Turn on Measure Tool"
          tooltipPlacement="right"
        />

        {/* Depth Estimation Control (conditional) */}
        {showDepthEstimation && (
          <ControlPanelButton
            onClick={() => setDepthStatus(DepthEstimationStatus.IN_PROGRESS)}
            disabled={
              !canCalculateDepth ||
              waitingForDepth !== DepthEstimationStatus.NOT_STARTED
            }
            activeTooltip="Estimate Depth"
            inactiveTooltip={
              waitingForDepth === DepthEstimationStatus.IN_PROGRESS
                ? "Estimating Depth..."
                : "Depth Estimation Complete"
            }
            icon={<CalculateIcon />}
            tooltipPlacement="right"
          />
        )}

        <Divider />

        {/* View and Model Controls */}
        <ControlPanelButton
          disabled={!modelLoaded}
          icon={<CenterFocusStrongIcon />}
          onClick={onCenterModel}
          activeTooltip="Center Model"
          inactiveTooltip="Center Model"
          tooltipPlacement="right"
        />

        {/* Point Management Controls */}
        <ControlPanelButton
          disabled={!canReset}
          icon={<RestartAltIcon />}
          onClick={onUndo}
          activeTooltip="Undo Last Point"
          inactiveTooltip="Undo Last Point"
          tooltipPlacement="right"
        />

        <ControlPanelButton
          disabled={!canReset}
          icon={<CancelIcon />}
          onClick={onReset}
          activeTooltip="Reset Points"
          inactiveTooltip="Reset Points"
          tooltipPlacement="right"
        />

        {/* Visual Customization */}
        <ColorSelector
          showLabel={false}
          showNames={false}
          selectedColor={color}
          onSelectColor={handleColorChange}
          selectSX={{
            backgroundColor: "white"
          }}
        />

        <Divider />

        {/* File Operations */}
        <UploadPointsButton
          disabled={uploadDisabled}
          onPointsUploaded={onPointsUploaded}
        />
        <DownloadPointsButton
          disabled={downloadDisabled}
          points={points}
          area={area}
          patientId={patientId}
        />
      </Stack>
    );
  };

export default ControlPanel;
